import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Download,
  FileText,
  Loader2,
  KeyRound,
  X,
  Upload,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";

const blankStudent = {
  name: "",
  fatherName: "",
  motherName: "",
  dob: "",
  mobile: "",
  address: "",
  enrollmentNumber: "",
  certificateId: "",
  courseName: "",
  duration: "",
  admissionDate: "",
  completionDate: "",
  marks: 0,
  maxMarks: 100,
  grade: "",
  status: "Valid",
};

const blankCourse = {
  slug: "",
  name: "",
  duration: "",
  tag: "Career",
  description: "",
  active: true,
};

const blankBlog = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  published: true,
};

const blankHead = {
  loginId: "",
  password: "",
  name: "",
};

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  disabled = false,
}) 

{
  return (
    <input
      className={`input-dark ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      }`}
      type={type}
      placeholder={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
    />
  );
}

export default function Portal() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();

  const [students, setStudents] = useState([]);
  const [heads, setHeads] = useState([]);
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState([]);

  const [tab, setTab] = useState("students");

  const [studentForm, setStudentForm] = useState(blankStudent);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentPhotoFile, setStudentPhotoFile] = useState(null);
  const [studentPhotoPreview, setStudentPhotoPreview] = useState("");

  const [loading, setLoading] = useState(false);

  // CENTER HEAD MANAGEMENT
  const [headForm, setHeadForm] = useState(blankHead);
  const [editingHead, setEditingHead] = useState(null);
  const [savingHead, setSavingHead] = useState(false);

  const [courseForm, setCourseForm] = useState(blankCourse);
  const [editingCourse, setEditingCourse] = useState(null);

  const [blogForm, setBlogForm] = useState(blankBlog);
  const [editingBlog, setEditingBlog] = useState(null);

  const isAdmin = user?.role === "admin";
  const isStudent = user?.role === "student";

  useEffect(() => {
    if (!user) return;

    loadStudents();

    if (user.role === "admin") {
      loadHeads();
      loadCourses();
      loadBlogs();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadStudents = async () => {
    setLoading(true);

    try {
      const response = await api.get("/students");
      setStudents(response.data || []);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Failed to load students"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadHeads = async () => {
    try {
      const response = await api.get("/center-heads");
      setHeads(response.data || []);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Failed to load center heads"
      );
    }
  };

  const loadCourses = async () => {
    try {
      const response = await api.get("/courses/all");
      setCourses(response.data || []);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Failed to load courses"
      );
    }
  };

  const loadBlogs = async () => {
    try {
      const response = await api.get("/blogs/all");
      setBlogs(response.data || []);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Failed to load blogs"
      );
    }
  };

// ---------------- STUDENTS ----------------

const saveStudent = async (event) => {
  event.preventDefault();

  try {
    const payload = {
      ...studentForm,
      marks: Number(studentForm.marks) || 0,
      maxMarks: Number(studentForm.maxMarks) || 100,
    };

    let response;

    if (editingStudent) {
      response = await api.put(
        `/students/${editingStudent.id}`,
        payload
      );
    } else {
      response = await api.post("/students", payload);
    }

    // Upload student photo after the student record is created/updated
    if (studentPhotoFile && response.data?.id) {
      const formData = new FormData();
      formData.append("file", studentPhotoFile);

      await api.post(
        `/students/${response.data.id}/photo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Student photo uploaded successfully");
    }

    if (response.data?.generatedPassword) {
      toast.success(
        `Student login: ${response.data.loginId} | Password: ${response.data.generatedPassword}`,
        {
          duration: 10000,
        }
      );
    } else {
      toast.success("Student saved successfully");
    }

    setShowStudentForm(false);
    setEditingStudent(null);
    setStudentForm(blankStudent);

    // Clear selected photo and preview
    setStudentPhotoFile(null);
    setStudentPhotoPreview("");

    loadStudents();
  } catch (error) {
    toast.error(
      formatApiErrorDetail(error.response?.data?.detail) ||
        "Student save failed"
    );
  }
};

// ---------------- CENTER HEADS ----------------

  const startCreateHead = () => {
    setEditingHead(null);
    setHeadForm(blankHead);
  };

  const startEditHead = (head) => {
    setEditingHead(head);

    setHeadForm({
      loginId: head.loginId || "",
      password: "",
      name: head.name || "",
    });
  };

  const cancelHeadEdit = () => {
    setEditingHead(null);
    setHeadForm(blankHead);
  };

const deleteHead = async (head) => {
  const confirmed = window.confirm(
    `Delete ${head.name} (${head.loginId})?\n\nThis will permanently remove the Center Head login access.`
  );

  if (!confirmed) return;

  try {
    await api.delete(
      `/center-heads/${encodeURIComponent(head.loginId)}`
    );

    toast.success("Center Head deleted successfully");

    if (editingHead?.loginId === head.loginId) {
      cancelHeadEdit();
    }

    loadHeads();
  } catch (error) {
    toast.error(
      formatApiErrorDetail(error.response?.data?.detail) ||
        "Failed to delete Center Head"
    );
  }
};

  const saveHead = async (event) => {
    event.preventDefault();

    if (!headForm.name.trim()) {
      toast.error("Enter Center Head name");
      return;
    }

    if (!editingHead) {
      if (!headForm.loginId.trim()) {
        toast.error("Enter Login ID");
        return;
      }

      if (!headForm.password.trim()) {
        toast.error("Enter password");
        return;
      }
    }

    setSavingHead(true);

    try {
      if (editingHead) {
        const payload = {
          name: headForm.name.trim(),
        };

        // Only send password when admin entered a new one
        if (headForm.password.trim()) {
          payload.password = headForm.password;
        }

        await api.put(
          `/center-heads/${encodeURIComponent(
            editingHead.loginId
          )}`,
          payload
        );

        toast.success("Center Head updated successfully");
      } else {
        const response = await api.post("/center-heads", {
          loginId: headForm.loginId.trim(),
          password: headForm.password,
          name: headForm.name.trim(),
        });

        toast.success(
          `Center created: ${response.data.loginId} / ${response.data.password}`,
          { duration: 10000 }
        );
      }

      setHeadForm(blankHead);
      setEditingHead(null);
      loadHeads();
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Center management operation failed"
      );
    } finally {
      setSavingHead(false);
    }
  };

  // ---------------- COURSES ----------------

  const saveCourse = async (event) => {
    event.preventDefault();

    try {
      if (editingCourse) {
        await api.put(
          `/courses/${editingCourse.slug}`,
          courseForm
        );

        toast.success("Course updated");
      } else {
        await api.post("/courses", courseForm);
        toast.success("Course added");
      }

      setCourseForm(blankCourse);
      setEditingCourse(null);

      loadCourses();
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Course operation failed"
      );
    }
  };

  const deleteCourse = async (course) => {
    if (!window.confirm(`Delete ${course.name}?`)) return;

    try {
      await api.delete(`/courses/${course.slug}`);

      toast.success("Course deleted");

      loadCourses();
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Course deletion failed"
      );
    }
  };

  // ---------------- BLOGS ----------------

  const saveBlog = async (event) => {
    event.preventDefault();

    try {
      if (editingBlog) {
        await api.put(
          `/blogs/${editingBlog.id}`,
          blogForm
        );

        toast.success("Blog updated");
      } else {
        await api.post("/blogs", blogForm);
        toast.success("Blog published");
      }

      setBlogForm(blankBlog);
      setEditingBlog(null);

      loadBlogs();
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Blog operation failed"
      );
    }
  };

  const deleteBlog = async (blog) => {
    if (!window.confirm(`Delete “${blog.title}”?`)) return;

    try {
      await api.delete(`/blogs/${blog.id}`);

      toast.success("Blog deleted");

      loadBlogs();
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Blog deletion failed"
      );
    }
  };

  // ---------------- MARKSHEET ----------------

  const downloadMarksheet = (student) => {
    window.open(
      `${api.defaults.baseURL}/marksheet/${student.enrollmentNumber}`,
      "_blank"
    );
  };

  const resetStudentPassword = async (student) => {
    try {
      const response = await api.post(
        `/students/${student.id}/reset-password`
      );

      toast.success(
        `${response.data.loginId} / ${response.data.password}`,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Reset failed"
      );
    }
  };

  // ---------------- AUTH ----------------

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-deep-black text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const tabs = isAdmin
    ? ["students", "centers", "courses", "blogs"]
    : ["students"];

  return (
    <div className="min-h-screen bg-deep-black">
      <Navbar />

      <Toaster
        theme="dark"
        position="top-right"
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* HEADER */}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-neon-green">
              / {user.role.replace("_", " ")} panel
            </p>

            <h1 className="mt-2 text-3xl font-bold text-white">
              Login Dashboard
            </h1>

            <p className="text-sm text-white/50">
              Signed in as {user.name || user.loginId}
            </p>
          </div>

          <button
            onClick={() => {
              logout();
              nav("/");
            }}
            className="btn-ghost-neon !px-4 !py-2 text-sm"
          >
            <LogOut
              size={15}
              className="mr-2"
            />
            Logout
          </button>
        </div>

        {/* TABS */}

        <div className="mt-7 flex gap-2 overflow-x-auto pb-2">
          {tabs.map((item) => (
            <button
              key={item}
              onClick={() => setTab(item)}
              className={`rounded-full px-4 py-2 text-sm ${
                tab === item
                  ? "bg-neon-green text-black"
                  : "bg-white/5 text-white/65"
              }`}
            >
              {item[0].toUpperCase() + item.slice(1)}
            </button>
          ))}
        </div>

        {/* ================= STUDENTS ================= */}

        {tab === "students" && (
          <section className="mt-6">
            <div className="flex justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {isStudent
                    ? "My Details"
                    : "Student Database"}
                </h2>

                {isStudent && (
                  <p className="mt-1 text-sm text-white/45">
                    Your course, duration, marks and marksheet
                    are shown here.
                  </p>
                )}
              </div>

              {!isStudent && (
                <button
  className="btn-neon !px-4 !py-2 text-sm"
  onClick={() => {
    setEditingStudent(null);
    setStudentForm(blankStudent);
    setStudentPhotoFile(null);
    setStudentPhotoPreview("");
    setShowStudentForm(true);
  }}
>
                  <Plus
                    size={15}
                    className="mr-2"
                  />
                  Create Marksheet
                </button>
              )}
            </div>

            <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-white/5 text-xs uppercase text-white/50">
                  <tr>
                    {[
                      "Student",
                      "Enrollment",
                      "Course",
                      "Duration",
                      "Marks",
                      "Login",
                      "Actions",
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="px-4 py-3"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-white/5">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center"
                      >
                        <Loader2 className="mx-auto animate-spin" />
                      </td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center text-white/40"
                      >
                        No student records found.
                      </td>
                    </tr>
                  ) : (
                    students.map((student) => (
                      <tr key={student.id}>
                        <td className="px-4 py-3 text-white">
                          {student.name}

                          <div className="text-xs text-white/40">
                            {student.fatherName}
                          </div>
                        </td>

                        <td className="px-4 py-3 font-mono text-neon-green">
                          {student.enrollmentNumber}
                        </td>

                        <td className="px-4 py-3 text-white/75">
                          {student.courseName}
                        </td>

                        <td className="px-4 py-3 text-white/60">
                          {student.duration}
                        </td>

                        <td className="px-4 py-3">
                          {student.marks}/
                          {student.maxMarks} ·{" "}
                          {student.grade || "-"} (
                          {student.percentage ?? 0}%)
                        </td>

                        <td className="px-4 py-3 font-mono text-xs">
                          {student.loginId}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {!isStudent && (
                              <button
                                title="Edit student"
                                className="grid h-8 w-8 place-items-center rounded border border-white/10"
                                onClick={() => {
                                setEditingStudent(student);

                                    setStudentForm({
                                    ...blankStudent,
                                    ...student,
                                      });

                                    setStudentPhotoFile(null);

                                      setStudentPhotoPreview(
                                      student.studentPhoto
                                      ? `${api.defaults.baseURL}${student.studentPhoto}`
                                      : ""
                                      );

                                      setShowStudentForm(true);
                                      }}
                                      >
                                <Pencil size={13} />
                              </button>
                            )}

                            <button
                              title="Download marksheet"
                              className="grid h-8 w-8 place-items-center rounded border border-white/10"
                              onClick={() =>
                                downloadMarksheet(student)
                              }
                            >
                              <Download size={13} />
                            </button>

                            {isAdmin && (
                              <button
                                title="Reset student password"
                                className="grid h-8 w-8 place-items-center rounded border border-white/10"
                                onClick={() =>
                                  resetStudentPassword(student)
                                }
                              >
                                <KeyRound size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ================= CENTER MANAGEMENT ================= */}

        {tab === "centers" && isAdmin && (
          <section className="mt-6 grid gap-5 lg:grid-cols-2">
            {/* CREATE / EDIT FORM */}

            <div className="glass rounded-2xl p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {editingHead
                      ? "Edit Center Head"
                      : "Create Center Head"}
                  </h2>

                  <p className="mt-1 text-sm text-white/40">
                    {editingHead
                      ? "Update the Center Head name or set a new password."
                      : "Create login credentials for a new Center Head."}
                  </p>
                </div>

                {editingHead && (
                  <button
                    type="button"
                    onClick={cancelHeadEdit}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-white/60 hover:text-white"
                    title="Cancel edit"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              <form
                onSubmit={saveHead}
                className="mt-5 space-y-3"
              >
                <Field
                  label="Center Head Name"
                  value={headForm.name}
                  onChange={(value) =>
                    setHeadForm({
                      ...headForm,
                      name: value,
                    })
                  }
                  required
                />

                <Field
                  label="Login ID"
                  value={headForm.loginId}
                  onChange={(value) =>
                    setHeadForm({
                      ...headForm,
                      loginId: value.toUpperCase(),
                    })
                  }
                  required={!editingHead}
                  disabled={!!editingHead}
                />

                {editingHead && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                    <div className="text-[11px] uppercase tracking-widest text-white/40">
                      Center ID
                    </div>

                    <div className="mt-1 font-mono text-sm text-neon-green">
                      {editingHead.centerId}
                    </div>
                  </div>
                )}

                <Field
                  label={
                    editingHead
                      ? "New Password (optional)"
                      : "Password"
                  }
                  type="password"
                  value={headForm.password}
                  onChange={(value) =>
                    setHeadForm({
                      ...headForm,
                      password: value,
                    })
                  }
                  required={!editingHead}
                />

                {editingHead && (
                  <p className="text-xs text-white/40">
                    Leave the password blank to keep the current
                    password.
                  </p>
                )}

                <div className="flex gap-2">
                  <button
                    disabled={savingHead}
                    className="btn-neon flex-1 disabled:opacity-60"
                  >
                    {savingHead ? (
                      <Loader2
                        size={15}
                        className="mr-2 animate-spin"
                      />
                    ) : editingHead ? (
                      <Pencil
                        size={15}
                        className="mr-2"
                      />
                    ) : (
                      <Plus
                        size={15}
                        className="mr-2"
                      />
                    )}

                    {savingHead
                      ? "Saving..."
                      : editingHead
                      ? "Update Center Head"
                      : "Create Center Credentials"}
                  </button>

                  {editingHead && (
                    <button
                      type="button"
                      className="btn-ghost-neon"
                      onClick={cancelHeadEdit}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* CENTER HEAD LIST */}

{/* CENTER HEAD LIST */}

<div className="space-y-3">
  <div className="flex items-center justify-between">
    <div>
      <h2 className="text-xl font-semibold text-white">
        Center Heads
      </h2>

      <p className="mt-1 text-sm text-white/40">
        {heads.length} center head
        {heads.length === 1 ? "" : "s"} registered
      </p>
    </div>
  </div>

  {heads.length === 0 ? (
    <div className="glass rounded-2xl p-6 text-white/40">
      No center heads created yet.
    </div>
  ) : (
    heads.map((head) => (
      <div
        key={head.loginId}
        className={`glass rounded-2xl p-5 transition ${
          editingHead?.loginId === head.loginId
            ? "border border-neon-green/40"
            : ""
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-white">
              {head.name}
            </div>

            <div className="mt-1 font-mono text-sm text-neon-green">
              {head.loginId}
            </div>

            <div className="mt-1 text-xs text-white/40">
              Center ID:{" "}
              <span className="font-mono">
                {head.centerId}
              </span>
            </div>

            {head.createdAt && (
              <div className="mt-1 text-[11px] text-white/30">
                Created:{" "}
                {new Date(
                  head.createdAt
                ).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              title="Edit Center Head"
              onClick={() => startEditHead(head)}
              className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/70 transition hover:border-neon-green/40 hover:text-neon-green"
            >
              <Pencil size={13} />
              Edit
            </button>

            <button
              type="button"
              title="Delete Center Head"
              onClick={() => deleteHead(head)}
              className="flex items-center gap-2 rounded-lg border border-red-500/20 px-3 py-2 text-xs text-white/70 transition hover:border-red-500/50 hover:text-red-400"
            >
              <Trash2 size={13} />
              Delete
            </button>
          </div>
        </div>
      </div>
    ))
  )}
</div>
</section>
)}
        {/* ================= COURSES ================= */}

        {tab === "courses" && isAdmin && (
          <section className="mt-6 grid gap-5 lg:grid-cols-2">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white">
                {editingCourse
                  ? "Edit Course"
                  : "Manage Courses"}
              </h2>

              <form
                onSubmit={saveCourse}
                className="mt-5 space-y-3"
              >
                <Field
                  label="Slug"
                  value={courseForm.slug}
                  onChange={(value) =>
                    setCourseForm({
                      ...courseForm,
                      slug: value,
                    })
                  }
                  required
                  disabled={!!editingCourse}
                />

                <Field
                  label="Course Name"
                  value={courseForm.name}
                  onChange={(value) =>
                    setCourseForm({
                      ...courseForm,
                      name: value,
                    })
                  }
                  required
                />

                <Field
                  label="Duration"
                  value={courseForm.duration}
                  onChange={(value) =>
                    setCourseForm({
                      ...courseForm,
                      duration: value,
                    })
                  }
                  required
                />

                <Field
                  label="Tag"
                  value={courseForm.tag}
                  onChange={(value) =>
                    setCourseForm({
                      ...courseForm,
                      tag: value,
                    })
                  }
                  required
                />

                <textarea
                  className="input-dark min-h-28"
                  placeholder="Description"
                  value={courseForm.description}
                  onChange={(e) =>
                    setCourseForm({
                      ...courseForm,
                      description: e.target.value,
                    })
                  }
                />

                <div className="flex gap-2">
                  <button className="btn-neon flex-1">
                    {editingCourse
                      ? "Update Course"
                      : "Add Course"}
                  </button>

                  {editingCourse && (
                    <button
                      type="button"
                      className="btn-ghost-neon"
                      onClick={() => {
                        setEditingCourse(null);
                        setCourseForm(blankCourse);
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-3">
              {courses.length === 0 ? (
                <div className="glass rounded-2xl p-6 text-white/40">
                  No courses in Google Sheets yet.
                </div>
              ) : (
                courses.map((course) => (
                  <div
                    key={course.slug}
                    className="glass rounded-2xl p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-white">
                          {course.name}
                        </div>

                        <div className="mt-1 text-sm text-white/55">
                          {course.duration} · {course.tag}
                        </div>

                        {course.description && (
                          <p className="mt-2 text-xs text-white/40">
                            {course.description}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-1">
                        <button
                          title="Edit course"
                          className="grid h-8 w-8 place-items-center rounded border border-white/10"
                          onClick={() => {
                            setEditingCourse(course);
                            setCourseForm({
                              ...blankCourse,
                              ...course,
                            });
                          }}
                        >
                          <Pencil size={13} />
                        </button>

                        <button
                          title="Delete course"
                          className="grid h-8 w-8 place-items-center rounded border border-white/10"
                          onClick={() =>
                            deleteCourse(course)
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {/* ================= BLOGS ================= */}

        {tab === "blogs" && isAdmin && (
          <section className="mt-6 grid gap-5 lg:grid-cols-2">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white">
                {editingBlog
                  ? "Edit Blog"
                  : "Write Blog"}
              </h2>

              <form
                onSubmit={saveBlog}
                className="mt-5 space-y-3"
              >
                <Field
                  label="Title"
                  value={blogForm.title}
                  onChange={(value) =>
                    setBlogForm({
                      ...blogForm,
                      title: value,
                    })
                  }
                  required
                />

                <Field
                  label="Slug"
                  value={blogForm.slug}
                  onChange={(value) =>
                    setBlogForm({
                      ...blogForm,
                      slug: value,
                    })
                  }
                  required
                />

                <Field
                  label="Short excerpt"
                  value={blogForm.excerpt}
                  onChange={(value) =>
                    setBlogForm({
                      ...blogForm,
                      excerpt: value,
                    })
                  }
                />

                <textarea
                  className="input-dark min-h-48"
                  placeholder="Blog content"
                  value={blogForm.content}
                  onChange={(e) =>
                    setBlogForm({
                      ...blogForm,
                      content: e.target.value,
                    })
                  }
                  required
                />

                <div className="flex gap-2">
                  <button className="btn-neon flex-1">
                    <FileText
                      size={15}
                      className="mr-2"
                    />

                    {editingBlog
                      ? "Update Blog"
                      : "Publish Blog"}
                  </button>

                  {editingBlog && (
                    <button
                      type="button"
                      className="btn-ghost-neon"
                      onClick={() => {
                        setEditingBlog(null);
                        setBlogForm(blankBlog);
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-3">
              {blogs.length === 0 ? (
                <div className="glass rounded-2xl p-6 text-white/40">
                  No blogs published yet.
                </div>
              ) : (
                blogs.map((blog) => (
                  <article
                    key={blog.id}
                    className="glass rounded-2xl p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-white">
                          {blog.title}
                        </h3>

                        <p className="mt-2 text-sm text-white/55">
                          {blog.excerpt}
                        </p>
                      </div>

                      <div className="flex gap-1">
                        <button
                          title="Edit blog"
                          className="grid h-8 w-8 place-items-center rounded border border-white/10"
                          onClick={() => {
                            setEditingBlog(blog);
                            setBlogForm({
                              ...blankBlog,
                              ...blog,
                            });
                          }}
                        >
                          <Pencil size={13} />
                        </button>

                        <button
                          title="Delete blog"
                          className="grid h-8 w-8 place-items-center rounded border border-white/10"
                          onClick={() =>
                            deleteBlog(blog)
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        )}
      </main>

      {/* ================= STUDENT MODAL ================= */}

      {showStudentForm && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-neon-green/20 bg-deep-black p-6">
            <div className="flex justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingStudent
                  ? "Edit Marksheet"
                  : "Create Marksheet"}
              </h2>

              <button
                type="button"
                className="text-white/60"
                onClick={() =>
                  setShowStudentForm(false)
                }
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={saveStudent}
              className="mt-5 grid gap-3 md:grid-cols-2"
            >
{Object.entries(blankStudent)
  .filter(
    ([key]) =>
      ![
        "certificateId",
        "grade",
        "status",
      ].includes(key)
  )
  .map(([key]) => (
    <Field
      key={key}
      label={key}
      type={
        ["marks", "maxMarks"].includes(key)
          ? "number"
          : [
              "dob",
              "admissionDate",
              "completionDate",
            ].includes(key)
          ? "date"
          : "text"
      }
      value={studentForm[key]}
      onChange={(value) =>
        setStudentForm({
          ...studentForm,
          [key]: value,
        })
      }
      required={[
        "name",
        "fatherName",
        "dob",
        "enrollmentNumber",
        "courseName",
        "duration",
        "admissionDate",
        "completionDate",
      ].includes(key)}
    />
  ))}

{/* ================= STUDENT PHOTO ================= */}

<div className="md:col-span-2">
  <label className="mb-2 block text-xs uppercase tracking-widest text-white/60">
    Student Photo
  </label>

  <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-5 sm:flex-row sm:items-center">
    
    {/* PHOTO PREVIEW */}
    <div className="h-32 w-28 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black">
      {studentPhotoPreview ? (
        <img
          src={studentPhotoPreview}
          alt="Student Preview"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="grid h-full w-full place-items-center text-center text-xs text-white/30">
          No Photo
        </div>
      )}
    </div>

    {/* UPLOAD AREA */}
    <div className="flex-1">
      <p className="text-sm text-white/70">
        Upload Student Photograph
      </p>

      <p className="mt-1 text-xs text-white/40">
        JPG, JPEG, PNG or WEBP
      </p>

      <label className="mt-4 inline-flex cursor-pointer items-center rounded-lg border border-neon-green/30 px-4 py-2 text-sm text-neon-green transition hover:bg-neon-green/10">
        <Upload
          size={15}
          className="mr-2"
        />

        {studentPhotoFile
          ? "Change Photo"
          : "Choose Photo"}

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];

            if (!file) return;

            setStudentPhotoFile(file);

            const previewUrl =
              URL.createObjectURL(file);

            setStudentPhotoPreview(previewUrl);
          }}
        />
      </label>

      {studentPhotoFile && (
        <p className="mt-2 text-xs text-white/40">
          Selected: {studentPhotoFile.name}
        </p>
      )}
    </div>
  </div>
</div>

{/* STATUS */}
<select
  className="input-dark"
  value={studentForm.status}
  onChange={(e) =>
    setStudentForm({
      ...studentForm,
      status: e.target.value,
    })
  }
>
  <option>Valid</option>
  <option>Pending</option>
  <option>Revoked</option>
</select>

              <div className="flex justify-end gap-2 md:col-span-2">
                <button
                  type="button"
                  onClick={() =>
                    setShowStudentForm(false)
                  }
                  className="btn-ghost-neon"
                >
                  Cancel
                </button>

                <button className="btn-neon">
                  {editingStudent
                    ? "Save Changes"
                    : "Save & Generate Credentials"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}